/** Header personalization: My Score cup widget + profile chip with dropdown. */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Trophy, User as UserIcon, Settings as SettingsIcon, LogOut, Shield, Volume2, VolumeX, Crown, Contrast } from "lucide-react";
import { setCelebrationWorkspace, isCelebrationMuted, setCelebrationMuted } from "@/lib/celebrate";
import useWorkspacePerms from "@/lib/useWorkspacePerms";
import HeaderHrWidgets from "@/components/HeaderHrWidgets";
import { useTheme } from "@/lib/theme";

export default function HeaderProfile() {
  const { user, activeWorkspaceId, logout } = useAuth();
  const { navOverrides, hasPerm } = useWorkspacePerms();
  const nav = useNavigate();
  const [prof, setProf] = useState(null);
  const [muted, setMuted] = useState(isCelebrationMuted());
  const { highContrast, setHighContrast } = useTheme();

  const load = useCallback(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/me/header-profile`).then((r) => setProf(r.data)).catch(() => {});
  }, [activeWorkspaceId]);

  useEffect(() => {
    setCelebrationWorkspace(activeWorkspaceId);
    load();
    window.addEventListener("acq-refresh-score", load);
    return () => window.removeEventListener("acq-refresh-score", load);
  }, [activeWorkspaceId, load]);

  const subtitle = prof?.designation || prof?.role_name || "";
  const points = prof?.points_total ?? 0;

  return (
    <div className="flex items-center gap-2">
      {/* R158w · Feb 2026 — Both HR widgets (Clock in/out button + Compliance dot)
          hidden together when `header-clock-in` nav override is set. */}
      {navOverrides?.["header-clock-in"] !== "hide" && (
        <div className="hidden sm:flex"><HeaderHrWidgets /></div>
      )}
      {/* My Score cup widget */}
      {navOverrides?.["header-my-score"] !== "hide" && (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="group relative hidden sm:flex items-center gap-1.5 pl-2 pr-3 h-8 border border-amber-500/40 bg-gradient-to-r from-amber-500/15 via-amber-400/10 to-transparent hover:from-amber-500/25 transition-colors"
            data-testid="header-my-score"
            title="My score"
          >
            <span className="relative flex items-center justify-center w-5 h-5">
              <Trophy className="w-4 h-4 text-amber-500 group-hover:scale-110 transition-transform" />
            </span>
            <span className="text-xs font-black font-mono text-amber-600 dark:text-amber-400" data-testid="header-score-points">
              {points >= 0 ? points : points}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <div className="px-3 py-3 text-center border-b border-border" data-testid="header-score-popover">
            <div className="mx-auto w-12 h-12 flex items-center justify-center border-2 border-amber-500/60 bg-amber-500/10 mb-2">
              {prof?.rank === 1 ? <Crown className="w-6 h-6 text-amber-500" /> : <Trophy className="w-6 h-6 text-amber-500" />}
            </div>
            <div className="text-2xl font-black font-mono">{points} <span className="text-xs font-semibold text-foreground/50">pts</span></div>
            <div className="text-[11px] text-foreground/60 mt-0.5">
              {prof?.rank ? <>Ranked <b>#{prof.rank}</b> of {prof.total_ranked}</> : "Not ranked yet — earn your first points!"}
            </div>
          </div>
          <DropdownMenuItem onClick={() => nav("/app/leaderboard")} data-testid="header-score-leaderboard">
            <Trophy className="w-4 h-4 mr-2 text-amber-500" /> Company leaderboard
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      )}

      {/* Profile chip + dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 h-8 pl-1 pr-2 hover:bg-muted/60 transition-colors" data-testid="header-profile-chip">
            <div className="w-6 h-6 bg-primary text-primary-foreground flex items-center justify-center text-[11px] font-bold overflow-hidden rounded-full">
              {prof?.avatar_data_url || user?.avatar_data_url ? (
                <img src={prof?.avatar_data_url || user?.avatar_data_url} alt="" className="w-full h-full object-cover" />
              ) : (
                (prof?.name || user?.name || "?")[0]?.toUpperCase()
              )}
            </div>
            <div className="hidden sm:block text-left leading-tight">
              <div className="text-xs font-semibold max-w-[140px] truncate" data-testid="header-profile-name">{prof?.name || user?.name}</div>
              {subtitle && <div className="text-[10px] text-foreground/55 max-w-[140px] truncate" data-testid="header-profile-designation">{subtitle}</div>}
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="leading-tight">
            <div className="text-sm">{prof?.name || user?.name}</div>
            <div className="text-[10px] font-normal text-foreground/55">{subtitle || user?.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {user?.is_super_admin && (
            <DropdownMenuItem onClick={() => nav("/super-admin")} data-testid="header-menu-superadmin">
              <Shield className="w-4 h-4 mr-2" /> Super Admin
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => nav("/app/profile")} data-testid="header-menu-profile">
            <UserIcon className="w-4 h-4 mr-2" /> My profile
          </DropdownMenuItem>
          {/* Iter Feb 2026 R33 · Workspace Settings visible only when
                user has permission — either legacy `workspace.manage`
                / `workspace.admin` or the new granular
                `workspace.settings.view`. */}
          {(user?.is_super_admin
              || hasPerm("workspace.admin")
              || hasPerm("workspace.manage")
              || hasPerm("workspace.settings.view")) && (
            <DropdownMenuItem onClick={() => nav("/app/settings")} data-testid="header-menu-settings">
              <SettingsIcon className="w-4 h-4 mr-2" /> Workspace settings
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={(e) => { e.preventDefault(); const v = !muted; setCelebrationMuted(v); setMuted(v); }}
            data-testid="header-menu-mute"
          >
            {muted ? <VolumeX className="w-4 h-4 mr-2" /> : <Volume2 className="w-4 h-4 mr-2" />}
            {muted ? "Unmute celebrations" : "Mute celebration sounds"}
          </DropdownMenuItem>
          {/* R118 · Feb 2026 — High-contrast (WCAG-AAA) toggle. */}
          <DropdownMenuItem
            onClick={(e) => { e.preventDefault(); setHighContrast(!highContrast); }}
            data-testid="header-menu-hc"
          >
            <Contrast className="w-4 h-4 mr-2" />
            {highContrast ? "Standard contrast" : "High contrast (WCAG-AAA)"}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} data-testid="header-menu-logout">
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
